import { useField, FormikErrors } from 'formik';
import { AttributeFormState } from '@idw-types/state/attribute-form-state';
import { FocusEvent, MouseEvent, useEffect, useMemo, useState, useRef } from 'react';
import { Logger } from '@utils/logger';
import { AttributeTypes } from '@iddataweb/idw-common-ui/enums';
import { useDispatch, useSelector } from 'react-redux';
import { getAsi, getMaxPinSendCount, getOOBPinUrl, getOidcCallbackURL, getRedirectToCallbackUrlOnError } from '@redux/selectors/verification-configuration-selectors';
import * as Yup from 'yup';
import { OOBStatusTypes } from '@enums/oob-status-types';
import { AttributeStatusTypes } from '@enums/attribute-status-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { setError } from '@redux/actions/error-actions';
import { getAttributeSubmitValue } from '@utils/attribute-data-util';
import { SendTelephonePinParamsType, sendTelephonePIN } from '@requests/send-telephone-pin';
import { SendEmailPinParamsType, sendEmailPIN } from '@requests/send-email-pin';
import { getAttempt, getAttributeData } from '@redux/selectors/verification-form-selectors';
import { PinInput } from './pin-input/pin-input';
import { handleNavigationForError } from '@utils/navigation-util';
import { getActiveLanguage } from '@redux/selectors/language-selectors';

type PinInputContainerProps = {
    attribute: AttributeFormState;
    fieldShouldBeAutofocused: boolean;
    attributeValuesIndex: number;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void> | Promise<FormikErrors<any>>;
    setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
    onBlur: (event: FocusEvent<HTMLInputElement>) => void;
    onSetSubmitValue: (data: any) => void;
    isSubmitting: boolean;
};

export const PinInputContainer = (props: PinInputContainerProps) => {
    const dispatch = useDispatch();
    const attributeData = useSelector(getAttributeData);
    const location = useLocation();
    const navigate = useNavigate();
    const redirectToCallbackUrlOnError = useSelector(getRedirectToCallbackUrlOnError);
    const oidcCallbackURL = useSelector(getOidcCallbackURL);

    const {
        attribute,
        fieldShouldBeAutofocused,
        attributeValuesIndex,
        setFieldValue,
        setFieldTouched,
        onBlur,
        onSetSubmitValue,
        isSubmitting
    } = props;

    const PIN_RESEND_INTERVAL = 30;
    const maxPinSendCount = useSelector(getMaxPinSendCount);
    const asi = useSelector(getAsi);
    const oobPinUrl = useSelector(getOOBPinUrl);
    const attempt = useSelector(getAttempt);
    const activeLanguage = useSelector(getActiveLanguage);
    const inputRef = useRef<HTMLInputElement>(null);
    const [resendEnabled, setResendEnabled] = useState(true);
    const [pinTimer, setPinTimer] = useState<NodeJS.Timeout>(null);
    const [pinSendCount, setPinSendCount] = useState(0);
    const [shouldSetFocus, setShouldSetFocus] = useState(true);
    const [sendingPin, setSendingPin] = useState(false);
    const [hasReachedMaxPinCount, setHasReachedMaxPinCount] = useState(false);

    useEffect(() => {
        if (inputRef.current && (shouldSetFocus || attempt > 1) && fieldShouldBeAutofocused) {
            setShouldSetFocus(false);
            setTimeout(() => {
                inputRef.current?.focus();
            }, 0);
        }
    }, [inputRef.current, shouldSetFocus, attempt, fieldShouldBeAutofocused]);

    useEffect(() => {
        resetResendTimer(3);
        return () => {
            clearTimeout(pinTimer);
        };
    }, []);

    const schema = useMemo(() => {
        return Yup.object().shape({
            pincode: Yup.string()
                .required(
                    attribute.hasInvalidPin
                        ? 'validation.pinCode.hasInvalidPin'
                        : 'validation.pinCode.required'
                )
                .test('isNumber', 'validation.pinCode.isNumber', (value: string) => /^\d+$/.test(value))
                .min(6, 'validation.pinCode.min')
        })
    }, [attribute.hasInvalidPin]);

    const validatePin = (value: string) => {
        //@ts-ignore
        return schema.validate({ pincode: value }).then(() => undefined).catch((error) => error.message);
    };

    const pincodeFieldName = useMemo(() => {
        return `attributeValues.${attributeValuesIndex}`;
    }, [attributeValuesIndex]);

    const [pinField, meta] = useField({
        name: pincodeFieldName,
        value: '',
        validate: validatePin
    });

    const resetResendTimer = (seconds: number) => {
        setResendEnabled(false);
        setPinTimer(setTimeout(enableResendTimer(), seconds * 1000));
    };

    const enableResendTimer = () => {
        return () => {
            Logger.info('[PinInput] enabeResendTimer');
            if (attribute.oobStatus !== OOBStatusTypes.COMPLETE) {
                setResendEnabled(true);
                Logger.info('[PinInput] enabeResendTimer resend enabled');
            }
        };
    };

    const canResendPin = useMemo(() => {
        if (!maxPinSendCount && !hasReachedMaxPinCount) {
            return true;
        } else if (!maxPinSendCount && hasReachedMaxPinCount) {
            return false;
        } else {
            return pinSendCount < maxPinSendCount;
        }
    }, [maxPinSendCount, hasReachedMaxPinCount, pinSendCount]);

    const sendPin = (event: MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        if (resendEnabled && canResendPin) {
            resetResendTimer(PIN_RESEND_INTERVAL);
            if (attribute.type === 'InternationalTelephone') {
                sendByPhone(attribute);
            } else {
                sendByEmail(attribute);
            }
            setPinSendCount((pinCount) => pinCount + 1);
            if (!canResendPin) {
                setResendEnabled(false);
            }
            if (inputRef.current) {
                inputRef.current?.focus();
            }
        }
    };

    const getPinSendMethod = (attributeFormStates: Array<AttributeFormState>) => {
        let sendMethod;

        const pinRelatedAttributes = attributeFormStates.filter((attribute) => {
            return (
                attribute.type === 'PINDeliveryPreference' ||
                attribute.type === 'EmailOrPhonePINPreference'
            );
        });
        if (pinRelatedAttributes.length) {
            const pinAttribute = pinRelatedAttributes[0];
            const pinProperties = ['emailorphonepinpreference', 'pindeliverypreference'];
            if (pinAttribute && pinAttribute.isValueFromPicker) {
                sendMethod = pinAttribute.pickerData.id;
            } else {
                const pinInputProperty = pinProperties.find(
                    (propertyName) => pinAttribute.inputData[propertyName]
                );
                sendMethod = pinAttribute.inputData[pinInputProperty];
            }
        }
        return sendMethod;
    };

    const handleErrorForSendPIN = (error: any, pinSendType: string) => {
        setSendingPin(false);
        Logger.warn(`[PinInput] Send PIN ${pinSendType} Error: ${error.errorCode} - ${error.message}`);
        if (error.errorCode === 'MAXIMUM_PIN_RESENDS_EXCEEDED') {
            resetResendTimer(PIN_RESEND_INTERVAL);
            setResendEnabled(false);
            setHasReachedMaxPinCount(true);
        } else {
            dispatch(setError(error));
            handleNavigationForError(dispatch)(
                redirectToCallbackUrlOnError,
                oidcCallbackURL,
                asi,
                "/error",
                navigate,
                location.pathname,
                location.search,
                error
            );
        }
    };

    const handleSendPINResponse = async (sendPINResponse: Response, pinSendType: string) => {
        Logger.info(`[PinInput] send ${pinSendType} PIN response`, sendPINResponse);

        const sendPINResponseJSON = await sendPINResponse.json();

        setSendingPin(false);
        Logger.info('[PinInput] Send PIN response JSON', sendPINResponseJSON);
        if (sendPINResponse.ok && sendPINResponseJSON.status === 'SUCCESS') {
            resetResendTimer(PIN_RESEND_INTERVAL);
            Logger.info('[PinInput] Send PIN success');
        } else {
            const error = {
                asi: asi,
                errorCode: sendPINResponseJSON?.errorCode || sendPINResponse.statusText,
                message: sendPINResponseJSON?.errorMessage || `Error sending ${pinSendType} PIN`,
                errorURI: sendPINResponse.url,
                status: `${sendPINResponse.status}`,
                forwardedErrorCode: sendPINResponseJSON?.errorCode,
                forwardedErrorDescription: sendPINResponseJSON?.errorMessage
            };
            handleErrorForSendPIN(error, pinSendType);
        }
    };

    const sendByPhone = async (attribute: AttributeFormState) => {
        let phoneNumber = null;
        let dialCode = null;
        let phoneNumberId: string | number = null;
        let method = 'sms';

        // if telephone is picker
        if (attribute.status === AttributeStatusTypes.PIN) {
            //state is pin, which means it was submitted, and now we should only use the stored id
            phoneNumberId = attribute.selectedAttributeId;
        } else if (attribute.status === AttributeStatusTypes.PICKER) {
            // grab the telephone id
            phoneNumberId = attribute.pickerData.id;
        } else {
            // if telephone is not picker, or pin
            // populate code and number
            phoneNumber = attribute.inputData.telephone;
            dialCode = attribute.inputData.dialCode;
        }
        let grabMethod;
        //if sms is picker
        if (attribute.attributeDependencyID) {
            //grab masked value
            grabMethod = attribute.pickerData.id;
        } else {
            //grab  method
            grabMethod = getPinSendMethod(attributeData);
        }
        if (grabMethod) {
            //@ts-ignore
            method = grabMethod;
        }
        //send pin
        setSendingPin(true);

        const params: SendTelephonePinParamsType = {
            attribute: attribute.id,
            method,
            dialCode,
            phoneNumber,
            phoneNumberId,
            asi,
            url: oobPinUrl,
            language: activeLanguage
        };
        Logger.info('[PinInput] Sending tel pin with params', params);

        try {
            const sendTelephonePINResponse = await sendTelephonePIN(params);
            await handleSendPINResponse(sendTelephonePINResponse, "SMS");
        } catch (error) {
            handleErrorForSendPIN(error, "SMS");
        }
    };

    const sendByEmail = async (attribute: AttributeFormState) => {
        const emailData = getAttributeSubmitValue(attribute);
        const params: SendEmailPinParamsType = {
            attribute: attribute.id,
            method: "EMAIL",
            emailAddress: null,
            firstName: null,
            emailAddressId: null,
            firstNameId: null,
            asi,
            url: oobPinUrl,
            language: activeLanguage
        };

        if (emailData === null) {
            Logger.warn('[PinInput] Failed to capture email data, cannot send PIN');
            return;
        }
        //check if data has id or email
        if (emailData.data.id !== -1) {
            setSendingPin(true);
            //@ts-ignore
            params.emailAddressId = emailData.data.id;

            try {
                const sendEmailPINResponse = await sendEmailPIN(params);
                await handleSendPINResponse(sendEmailPINResponse, "Email");
            } catch (error) {
                handleErrorForSendPIN(error, "Email");
            }
        } else {
            //grab dependent attribute value
            const fullNameAttribute = attributeData.find((attribute) => attribute.type === AttributeTypes.FULL_NAME);
            let name;

            if (fullNameAttribute) {
                if (fullNameAttribute.inputData.fname) {
                    name = fullNameAttribute.inputData.fname;
                } else if (fullNameAttribute.prefillValueMap.fname) {
                    name = fullNameAttribute.prefillValueMap.fname;
                } else {
                    name = null;
                }
            }
            let email;

            if (attribute.prefillValueMap.email) {
                email = attribute.prefillValueMap.email;
            } else if (attribute.inputData.email) {
                email = attribute.inputData.email;
            }
            //validate value
            if (email) {
                setSendingPin(true);
                params.emailAddress = email;
                params.firstName = name;

                try {
                    const sendEmailPINResponse = await sendEmailPIN(params);
                    await handleSendPINResponse(sendEmailPINResponse, "Email");
                } catch (error) {
                    handleErrorForSendPIN(error, "Email");
                }
            }
        }
    };

    const isALink = useMemo(() => hasReachedMaxPinCount || (resendEnabled && !sendingPin), [hasReachedMaxPinCount, resendEnabled, sendingPin]);

    const pinFieldTouched = meta.touched;
    const pinFieldError = meta.error;

    const pinAttribute = useMemo(() => {
        return {
            ...attribute,
            isDisabled: false
        };
    }, [attribute.inputData.pincode]);

    return (
        <div id={`${attribute.id}-responses`}>
            <PinInput
                inputRef={inputRef}
                attribute={pinAttribute}
                fieldName={pinField.name}
                fieldTouched={pinFieldTouched}
                fieldError={pinFieldError}
                onBlur={onBlur}
                setFieldTouched={setFieldTouched}
                setFieldValue={setFieldValue}
                isSubmitting={isSubmitting}
                onSetSubmitValue={onSetSubmitValue}
                isALink={isALink}
                canResendPin={canResendPin}
                resendEnabled={resendEnabled}
                sendingPin={sendingPin}
                sendPin={sendPin}
            />
        </div>
    );
};